/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { isSourceProduct3P } from '../../../../ai-answer-dialog/analytics-helpers';
import { onAIAnswerSourceClick } from '../../../../ai-answer-dialog/on-ai-answer-source-click';
import { SainAction } from '../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { AppIcon } from '../../../../common/ui/app-icon/app-icon';
import { type ObjectResolverBody } from '../../../common/hooks/use-fetch-smartlink-data';
import { type NLPSearchResultSource } from '../../../../ai-answer-dialog/types';

const toTitleCase = (sourceCategory: string) =>
	sourceCategory
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

interface DefinitionSourceProps {
	source: NLPSearchResultSource;
	sourceData: ObjectResolverBody;
	sourceIndex: number;
}

const styles = cssMap({
	root: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
	},
	sourceIconWrapperStyles: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '16px',
		height: '16px',
	},
});

export const DefinitionSource: ({
	source,
	sourceData,
	sourceIndex,
}: DefinitionSourceProps) => JSX.Element = ({
	source,
	sourceData,
	sourceIndex,
}: DefinitionSourceProps) => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const is1PSource = sourceData.data.generator?.name === 'Confluence';
	const sourceMetadata = is1PSource
		? sourceData.data.summary
		: sourceData.entityData?.type
			? toTitleCase(sourceData.entityData.type.category)
			: undefined;

	const onClickSource = useCallback(() => {
		const sourceProduct = sourceData.data.generator?.name.toLowerCase() || '';
		fireAnalyticsEvent(
			onAIAnswerSourceClick({
				sourceIndex,
				sourceProduct,
				is3PSource: isSourceProduct3P(sourceData.data.generator?.name),
				...commonAttributes,
			}),
		);
		if (fg('kd_definitions_source_product_clicked')) {
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				source,
				sourceProduct,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes]);

	return (
		<Anchor href={sourceData.data.url} xcss={styles.root} target="_blank" onClick={onClickSource}>
			<Stack space="space.100">
				<Inline alignBlock="center" space="space.100">
					{sourceData.data.generator && (
						<AppIcon app={sourceData.data.generator.name.toLowerCase()} size="xxsmall" />
					)}
					<Box>
						<Text color="color.text" maxLines={1} weight="medium">
							{sourceData.data.name}
						</Text>
					</Box>
				</Inline>
				<Box>
					<Text maxLines={2} color="color.text.subtle" size="small">
						{sourceMetadata}
					</Text>
				</Box>
			</Stack>
		</Anchor>
	);
};
